import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import * as authAPI from '../api/auth';

// 定义认证上下文的类型
interface AuthContextType {
  isAuthenticated: boolean;
  username: string | null;
  login: (username: string, password: string) => Promise<any>;
  logout: () => Promise<any>;
  updateCredentials: (currentPassword: string, newPassword: string, newUsername?: string) => Promise<any>;
}

// 创建认证上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// 创建一个自定义钩子，方便组件访问认证上下文
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// 认证提供者组件
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [username, setUsername] = useState<string | null>(null);

  // 初始化时从JWT令牌加载认证状态
  useEffect(() => {
    const token = authAPI.getStoredToken();
    if (token) {
      try {
        // 解析JWT令牌
        const decodedToken = authAPI.parseJwt(token);
        
        if (decodedToken) {
          // 检查令牌是否已过期
          const currentTime = Math.floor(Date.now() / 1000);
          if (decodedToken.exp && currentTime >= decodedToken.exp) {
            // 令牌已过期，清除存储
            console.log('存储的令牌已过期，清除认证状态');
            authAPI.clearTokens();
            return;
          }
          
          setIsAuthenticated(true);
          setUsername(decodedToken.username);
          
          // 初始化令牌自动刷新机制
          authAPI.setupTokenRefresh();
        } else {
          // 令牌无效，清除存储
          authAPI.clearTokens();
        }
      } catch (error) {
        console.error('Failed to parse auth token:', error);
        authAPI.clearTokens();
      }
    }
  }, []);

  // 登录方法
  const login = async (username: string, password: string) => {
    const result = await authAPI.login(username, password);
    if (result.success) {
      setIsAuthenticated(true);
      setUsername(result.data.user.username);
      
      // 初始化令牌自动刷新机制
      authAPI.setupTokenRefresh();
    }
    return result;
  };

  // 登出方法
  const logout = async () => {
    const result = await authAPI.logout();
    if (result.success) {
      setIsAuthenticated(false);
      setUsername(null);
    }
    return result;
  };

  // 更新用户凭据方法
  const updateCredentials = async (
    currentPassword: string, 
    newPassword: string, 
    newUsername?: string
  ) => {
    const result = await authAPI.updateCredentials(
      currentPassword, 
      newPassword, 
      newUsername
    );
    
    if (result.success && newUsername) {
      setUsername(newUsername);
    }
    
    return result;
  };

  // 提供认证上下文值
  const contextValue = {
    isAuthenticated,
    username,
    login,
    logout,
    updateCredentials,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};